import { slicedToArray as _slicedToArray, objectSpread2 as _objectSpread2, objectWithoutProperties as _objectWithoutProperties, extends as _extends } from '../../../_virtual/_rollupPluginBabelHelpers.esm.js';
import { useResizeObserver, filterDOMProps } from '@react-aria/utils';
import React, { forwardRef, createContext, useRef, useState, useCallback, useMemo } from 'react';
import { useFocusRing } from 'react-aria';
import { SelectContext, SelectStateContext, SelectValueContext, LabelContext, ButtonContext, OverlayTriggerStateContext, PopoverContext, GridListContext, ListStateContext, TextContext, FieldErrorContext, FormContext } from 'react-aria-components';
import { HiddenSelect } from './hidden-select.esm.js';
import { useSelect } from './use-select.esm.js';
import { useCollectionDocument, CollectionDocumentContext } from '../../collections/aria/collection.esm.js';
import { useContextProps, useSlottedContext, useSlot, removeDataAttributes, useRenderProps, Hidden, Provider } from '../../utils/index.esm.js';
import { useSelectState } from '../stately/use-select-state.esm.js';

var _excluded = ["labelProps", "triggerProps", "valueProps", "menuProps", "descriptionProps", "errorMessageProps"];
var CustomStateContext = /*#__PURE__*/createContext(null);
function Select(props, ref) {
  var _ref2, _props$validationBeha;
  var _useContextProps = useContextProps(props, ref, SelectContext);
  var _useContextProps2 = _slicedToArray(_useContextProps, 2);
  props = _useContextProps2[0];
  ref = _useContextProps2[1];
  var _ref = useSlottedContext(FormContext) || {},
    formValidationBehavior = _ref.validationBehavior;
  var validationBehavior = (_ref2 = (_props$validationBeha = props.validationBehavior) !== null && _props$validationBeha !== void 0 ? _props$validationBeha : formValidationBehavior) !== null && _ref2 !== void 0 ? _ref2 : 'native';
  var _useCollectionDocumen = useCollectionDocument(),
    collection = _useCollectionDocumen.collection,
    document = _useCollectionDocumen.document;
  var state = useSelectState(_objectSpread2(_objectSpread2({}, props), {}, {
    collection: collection,
    children: undefined,
    validationBehavior: validationBehavior
  }));
  var _useFocusRing = useFocusRing({
      within: true
    }),
    isFocusVisible = _useFocusRing.isFocusVisible,
    focusProps = _useFocusRing.focusProps;

  // Get props for child elements from useSelect
  var buttonRef = useRef(null);
  var _useSlot = useSlot(),
    _useSlot2 = _slicedToArray(_useSlot, 2),
    labelRef = _useSlot2[0],
    label = _useSlot2[1];
  var _useSelect = useSelect(_objectSpread2(_objectSpread2({}, removeDataAttributes(props)), {}, {
      label: label,
      validationBehavior: validationBehavior
    }), state, buttonRef),
    labelProps = _useSelect.labelProps,
    triggerProps = _useSelect.triggerProps,
    valueProps = _useSelect.valueProps,
    menuProps = _useSelect.menuProps,
    descriptionProps = _useSelect.descriptionProps,
    errorMessageProps = _useSelect.errorMessageProps,
    validation = _objectWithoutProperties(_useSelect, _excluded);

  // Make menu width match input + button
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    buttonWidth = _useState2[0],
    setButtonWidth = _useState2[1];
  var onResize = useCallback(function () {
    if (buttonRef.current) {
      setButtonWidth(buttonRef.current.offsetWidth + 'px');
    }
  }, [buttonRef]);
  useResizeObserver({
    ref: buttonRef,
    onResize: onResize
  });

  // Only expose a subset of state to renderProps function to avoid infinite render loop
  var renderPropsState = useMemo(function () {
    return {
      isOpen: state.isOpen,
      isFocused: state.isFocused,
      isFocusVisible: isFocusVisible,
      isDisabled: props.isDisabled || false,
      isInvalid: validation.isInvalid || false,
      isRequired: props.isRequired || false
    };
  }, [state.isOpen, state.isFocused, isFocusVisible, props.isDisabled, validation.isInvalid, props.isRequired]);
  var renderProps = useRenderProps(_objectSpread2(_objectSpread2({}, props), {}, {
    values: renderPropsState,
    defaultClassName: 'react-aria-Select'
  }));
  var DOMProps = filterDOMProps(props);
  delete DOMProps.id;
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Hidden, null, /*#__PURE__*/React.createElement(Provider
  // eslint-disable-next-line react-perf/jsx-no-new-array-as-prop
  , {
    values: [
    // @ts-ignore
    [SelectContext, props],
    // @ts-ignore
    [SelectStateContext, state], [CollectionDocumentContext, document]]
  }, renderProps.children)), /*#__PURE__*/React.createElement(Provider
  // eslint-disable-next-line react-perf/jsx-no-new-array-as-prop
  , {
    values: [
    // @ts-ignore
    [SelectContext, props],
    // @ts-ignore
    [SelectStateContext, state], [SelectValueContext, valueProps], [LabelContext, _objectSpread2(_objectSpread2({}, labelProps), {}, {
      ref: labelRef,
      elementType: 'span'
    })], [ButtonContext, _objectSpread2(_objectSpread2({}, triggerProps), {}, {
      ref: buttonRef,
      isPressed: state.isOpen
    })], [OverlayTriggerStateContext, state], [PopoverContext, {
      trigger: 'Select',
      triggerRef: buttonRef,
      placement: 'bottom start',
      style: {
        '--trigger-width': buttonWidth
      }
    }], [GridListContext, menuProps], [ListStateContext, state], [TextContext, {
      slots: {
        description: descriptionProps,
        errorMessage: errorMessageProps
      }
    }], [FieldErrorContext, validation], [CustomStateContext, {
      hasError: validation.isInvalid
    }]]
  }, /*#__PURE__*/React.createElement("div", _extends({}, DOMProps, renderProps, focusProps, {
    ref: ref,
    slot: props.slot || undefined,
    "data-focused": state.isFocused || undefined,
    "data-focus-visible": isFocusVisible || undefined,
    "data-open": state.isOpen || undefined,
    "data-disabled": props.isDisabled || undefined,
    "data-invalid": validation.isInvalid || undefined,
    "data-required": props.isRequired || undefined
  })), /*#__PURE__*/React.createElement(HiddenSelect, {
    state: state,
    triggerRef: buttonRef,
    label: label,
    name: props.name,
    isDisabled: props.isDisabled
  })));
}

/**
 * A select displays a collapsible list of options and allows a user to select one of them.
 */
var _Select = /*#__PURE__*/forwardRef(Select);

export { CustomStateContext, _Select as default };
//# sourceMappingURL=select.esm.js.map
